import { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
// Shared chrome: Escape closes, and the three management screens look alike
import { AdminModal as Modal, AdminField as Field } from '../../components/AdminModal';
import { apiErrorMessage } from '../../utils/apiError';
import {
  IoPersonAddOutline,
  IoTrashOutline,
  IoWarningOutline,
  IoCheckmarkCircleOutline,
  IoStarOutline
} from 'react-icons/io5';

export const FamilyCreateModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ name: '', description: '', ownerEmail: '', maxMembers: 20 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError(null);

      // The API wants the owner's id, so resolve the email first.
      const lookup = await adminApi.getUsers({ search: form.ownerEmail, limit: 5 });
      const owner = (lookup.data.users || []).find(
        (candidate) => candidate.email.toLowerCase() === form.ownerEmail.trim().toLowerCase()
      );
      if (!owner) {
        setError(`No user found with the email ${form.ownerEmail}.`);
        return;
      }

      await adminApi.createFamily({
        name: form.name,
        description: form.description || null,
        createdBy: owner.id,
        maxMembers: Number(form.maxMembers)
      });
      onSaved(`Created family "${form.name}" owned by ${owner.email}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create this family.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New family"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="family-create-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create family'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <form id="family-create-form" onSubmit={handleSubmit}>
        <Field label="Family name">
          <input type="text" value={form.name} onChange={set('name')} maxLength={100} required />
        </Field>
        <Field label="Description">
          <textarea value={form.description} onChange={set('description')} rows={2} />
        </Field>
        <Field label="Owner email" hint="This user becomes the first member, with their readers attached.">
          <input type="email" value={form.ownerEmail} onChange={set('ownerEmail')} required />
        </Field>
        <Field label="Maximum members">
          <input type="number" min="1" max="100" value={form.maxMembers} onChange={set('maxMembers')} required />
        </Field>
      </form>
    </Modal>
  );
};

export const FamilyEditModal = ({ family, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: family.name ?? '',
    description: family.description ?? '',
    maxMembers: family.maxMembers ?? 20,
    isActive: family.isActive !== false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const changes = {};
    if (form.name !== (family.name ?? '')) changes.name = form.name;
    if (form.description !== (family.description ?? '')) changes.description = form.description || null;
    if (Number(form.maxMembers) !== (family.maxMembers ?? 20)) changes.maxMembers = Number(form.maxMembers);
    if (form.isActive !== (family.isActive !== false)) changes.isActive = form.isActive;

    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await adminApi.updateFamily(family.id, changes);
      onSaved(`Updated family "${form.name}"`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this family.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Edit ${family.name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="family-edit-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <form id="family-edit-form" onSubmit={handleSubmit}>
        <Field label="Family name">
          <input type="text" value={form.name} onChange={set('name')} maxLength={100} required />
        </Field>
        <Field label="Description">
          <textarea value={form.description} onChange={set('description')} rows={2} />
        </Field>
        <Field label="Maximum members" hint="Cannot be set below the current member count.">
          <input type="number" min="1" max="100" value={form.maxMembers} onChange={set('maxMembers')} required />
        </Field>
        <label className="admin-modal-check">
          <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
          <span>Active</span>
          <em>Inactive families cannot be joined with an invite code.</em>
        </label>
      </form>
    </Modal>
  );
};

/**
 * The main support tool: add and remove members on their behalf, transfer
 * ownership, and repair the reader links that drift out of step.
 */
export const FamilyMembersModal = ({ family, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [detailResponse, integrityResponse] = await Promise.all([
        adminApi.getFamily(family.id),
        adminApi.getFamilyIntegrity(family.id)
      ]);
      setDetail(detailResponse.data.family);
      setIntegrity(integrityResponse.data.integrity);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load this family.'));
    } finally {
      setLoading(false);
    }
  }, [family.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (action, describe) => {
    try {
      setBusy(true);
      setError(null);
      setNotice(null);
      const result = await action();
      setNotice(describe(result));
      await load();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'That action did not go through.'));
    } finally {
      setBusy(false);
    }
  };

  const handleAddMember = (event) => {
    event.preventDefault();
    const email = newMemberEmail.trim();
    return run(
      () => adminApi.addFamilyMember(family.id, { email }),
      (result) => {
        setNewMemberEmail('');
        const attached = result?.data?.readersAttached ?? 0;
        return `Added ${email}${attached > 0 ? ` with ${attached} reader(s)` : ''}`;
      }
    );
  };

  const handleRemoveMember = (member) => run(
    () => adminApi.removeFamilyMember(family.id, member.id),
    (result) => `Removed ${member.email}${result?.data?.readersDetached > 0 ? ` and ${result.data.readersDetached} reader link(s)` : ''}`
  );

  const handleTransferOwner = (member) => run(
    () => adminApi.updateFamily(family.id, { createdBy: member.id }),
    () => `${member.email} now owns this family`
  );

  const handleRepair = () => run(
    () => adminApi.repairFamily(family.id),
    (result) => `Attached ${result.data.attached} and detached ${result.data.detached} reader link(s)`
  );

  const handleRegenerateCode = () => run(
    () => adminApi.regenerateFamilyInviteCode(family.id),
    (result) => `New invite code: ${result.data.family.inviteCode}`
  );

  const members = detail?.members || [];
  const readers = detail?.readers || [];

  return (
    <Modal
      wide
      title={`Members of ${family.name}`}
      onClose={onClose}
      footer={(
        <button type="button" className="admin-modal-btn" onClick={onClose}>Done</button>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}
      {notice && <div className="admin-modal-notice">{notice}</div>}

      {loading ? (
        <LoadingSpinner size="small" message="Loading members…" />
      ) : detail && (
        <>
          <div className="admin-modal-meta">
            <span>Invite code <code>{detail.inviteCode}</code></span>
            <span>{members.length} / {detail.maxMembers} members</span>
            <button type="button" className="admin-modal-link" onClick={handleRegenerateCode} disabled={busy}>
              Regenerate code
            </button>
          </div>

          {integrity && !integrity.isConsistent && (
            <div className="admin-modal-drift">
              <h4><IoWarningOutline /> Reader links are out of step</h4>
              <p>
                {integrity.missingReaderLinks.length > 0 && (
                  <>{integrity.missingReaderLinks.length} reader(s) belong to a member but are not in this family. </>
                )}
                {integrity.orphanedReaderLinks.length > 0 && (
                  <>{integrity.orphanedReaderLinks.length} reader(s) are linked here but their owner is not a member. </>
                )}
                This happens because joining attaches readers once and nothing re-syncs them afterwards.
              </p>
              <button type="button" className="admin-modal-btn is-primary" onClick={handleRepair} disabled={busy}>
                Repair links
              </button>
            </div>
          )}

          {integrity?.isConsistent && (
            <p className="admin-modal-ok">
              <IoCheckmarkCircleOutline /> Member and reader links agree.
            </p>
          )}

          <form className="admin-modal-add" onSubmit={handleAddMember}>
            <input
              type="email"
              value={newMemberEmail}
              onChange={(event) => setNewMemberEmail(event.target.value)}
              placeholder="Add a member by email"
              required
            />
            <button type="submit" className="admin-modal-btn is-primary" disabled={busy}>
              <IoPersonAddOutline /> Add
            </button>
          </form>
          <p className="admin-modal-hint">
            Adding a member attaches their active readers too, the same way joining with an invite
            code does.
          </p>

          <h4 className="admin-modal-subhead">Members</h4>
          <ul className="admin-modal-list">
            {members.map((member) => {
              const isOwner = member.id === detail.createdBy;
              return (
                <li key={member.id}>
                  <div className="admin-modal-list-main">
                    <strong>{member.name}</strong>
                    {isOwner && <span className="admin-modal-owner"><IoStarOutline /> Owner</span>}
                    <span className="admin-modal-sub">{member.email}</span>
                  </div>
                  <div className="admin-modal-list-actions">
                    {!isOwner && (
                      <button type="button" onClick={() => handleTransferOwner(member)} disabled={busy}>
                        Make owner
                      </button>
                    )}
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => handleRemoveMember(member)}
                      disabled={busy || isOwner}
                      title={isOwner ? 'Transfer ownership before removing the owner' : undefined}
                    >
                      <IoTrashOutline /> Remove
                    </button>
                  </div>
                </li>
              );
            })}
            {members.length === 0 && <li className="admin-modal-empty">No members.</li>}
          </ul>

          <h4 className="admin-modal-subhead">Readers in this family</h4>
          <ul className="admin-modal-list">
            {readers.map((reader) => (
              <li key={reader.id}>
                <div className="admin-modal-list-main">
                  <strong>{reader.name}</strong>
                  {!reader.isActive && <span className="admin-modal-sub">inactive</span>}
                  <span className="admin-modal-sub">owned by {reader.user?.email || 'unknown'}</span>
                </div>
                <div className="admin-modal-list-actions">
                  <button
                    type="button"
                    className="is-danger"
                    onClick={() => run(
                      () => adminApi.removeFamilyReader(family.id, reader.id),
                      () => `Detached ${reader.name}`
                    )}
                    disabled={busy}
                  >
                    <IoTrashOutline /> Detach
                  </button>
                </div>
              </li>
            ))}
            {readers.length === 0 && <li className="admin-modal-empty">No readers attached.</li>}
          </ul>
        </>
      )}
    </Modal>
  );
};

export const FamilyDeleteModal = ({ family, onClose, onDeleted }) => {
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      await adminApi.deleteFamily(family.id);
      onDeleted(`Deleted family "${family.name}"`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete this family.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      title={`Delete ${family.name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={deleting}>Cancel</button>
          <button
            type="button"
            className="admin-modal-btn is-danger"
            onClick={handleDelete}
            disabled={confirmation !== family.name || deleting}
          >
            {deleting ? 'Deleting…' : 'Delete family'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <p className="admin-modal-hint">
        This removes the grouping only. The {family.memberCount ?? 0} member(s) and
        {' '}{family.readerCount ?? 0} reader(s) keep their accounts and their whole reading history.
        Marking the family inactive instead keeps it around and can be undone.
      </p>

      <Field label={`Type ${family.name} to confirm`}>
        <input
          type="text"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          autoComplete="off"
        />
      </Field>
    </Modal>
  );
};
